import { create } from 'zustand';

import { tenantApi } from '@/api';
import { APP_CONST, AUTH_CONST, TENANT_CONST } from '@/constant';
import { storage } from '@/package';
import type {
  FetchTenantListResponse,
  Tenant,
  TenantStoreActions,
  TenantStoreState,
} from '@/type';
import { axiosInstance } from '@/util';

const getInitTenantList = () =>
  storage.local.get<Tenant[]>(TENANT_CONST.KEYS.STORAGE_TENANT_LIST) ?? [];
const getInitCurrentTenantID = () =>
  storage.local.get<string>(TENANT_CONST.KEYS.STORAGE_CURRENT_TENANT_ID) ?? '';

const INIT_STATE = {
  currentTenantID: getInitCurrentTenantID(),
  tenantList: getInitTenantList(),
  limitSize: {
    annotationLimit: 0,
    fileSizeLimit: 0,
    s3Limit: 0,
  },
  tenantCountInfoMap: {},
} as TenantStoreState;

const REGION =
  import.meta.env.MODE === 'development' ? 'ap-northeast-2' : 'us-east-1';

const processFetchResponse = async <T = any>(res: Response): Promise<T> => {
  switch (Math.floor(res.status / 100)) {
    case 4:
    case 5: {
      const data = await res.json();

      throw new Error(data.resultMsg, {
        cause: {
          url: res.url,
          status: res.status,
          statusText: res.statusText,
          data,
        },
      });
    }
    default:
      return (await res.json()).resultData;
  }
};

const useTenantStore = create<TenantStoreState & TenantStoreActions>(
  (set, get) => {
    // NOTE: provisioning 되기 이전의 테넌트 정보를 수정할 때 사용함
    let sessionKey: Nullish<string> = null;

    return {
      ...INIT_STATE,

      addTenant(tenant) {
        get().setTenantList(get().tenantList.concat(tenant));
      },

      checkVerifyCode({ email, code }) {
        return fetch(
          `${import.meta.env.VITE_APP_CONTROL_PLANE_URL}/user/email/verify`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, code }),
          },
        )
          .then<{
            customerAWSAccountId?: string;
            productCode?: string;
            sessionKey?: string;
          }>(processFetchResponse)
          .then(res => {
            if (res.sessionKey) {
              sessionKey = `${res.sessionKey}`;
            }

            return res;
          });
      },

      clear() {
        get().selectTenant();
        get().setTenantList([]);
      },

      fetchTenantList() {
        const idToken = storage.local.get<string>(
          AUTH_CONST.KEYS.STORAGE_ID_TOKEN,
        );

        if (!idToken) return Promise.reject(new Error('Access token is empty'));

        return fetch(`${import.meta.env.VITE_APP_CONTROL_PLANE_URL}/tenant`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        })
          .then(async res => {
            if (res.status === 200) return res;

            throw new Error((await res.json()).message);
          })
          .then(res => res.json() as Promise<FetchTenantListResponse>)
          .then(res => {
            get().setTenantList(res.resultData);

            return res;
          })
          .then(({ resultData: data }) => {
            data
              .filter(t => t.region)
              .map(t => {
                const { accessPath, region } = t;

                axiosInstance.defaults.baseURL = `${APP_CONST.APP_API_URL.replace(/http(s?):\/\//, token => `${token}${region}-`)}/${accessPath}/api`;
                axiosInstance.defaults.headers.common['Authorization'] =
                  `Bearer ${idToken}`;

                return tenantApi
                  .getTenantInfo(
                    {},
                    {
                      headers: {
                        'X-Tenant-ID': t.tenantID,
                      },
                    },
                  )
                  .then(res => {
                    set({
                      tenantCountInfoMap: {
                        ...get().tenantCountInfoMap,
                        [t.tenantID]: res.data.resultData,
                      },
                    });
                  });
              });
          });
      },

      getCurrentTenant() {
        const tenantId = get().currentTenantID || INIT_STATE.currentTenantID;

        return tenantId ? get().getTenant(tenantId) : null;
      },

      getTenant(tenantId) {
        return get().tenantList.find(tenant => tenant.tenantID === tenantId);
      },

      isDelegationMode() {
        return !!get().getCurrentTenant()?.delegation;
      },

      isExistTenant(tenantName) {
        return fetch(
          `${import.meta.env.VITE_APP_CONTROL_PLANE_URL}/tenant/search?tenantName=${tenantName}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
          .then(async res => {
            const data = await res.json();
            return !!data.resultData.length;
          })
          .catch(() => {
            return null;
          });
      },

      registerTenant({ tenantData, tenantRegistrationData }) {
        const headers = {
          'Content-Type': 'application/json',
        } as Dictionary<string>;

        if (sessionKey) {
          headers['X-Session-Key'] = sessionKey;
        }

        tenantRegistrationData.region ??= REGION;

        return fetch(
          `${import.meta.env.VITE_APP_CONTROL_PLANE_URL}/tenant/slack/provision`,
          {
            method: sessionKey ? 'PUT' : 'POST',
            headers,
            // oxlint-disable-next-line no-invalid-fetch-options
            body: JSON.stringify({
              tenantData: {
                registrationStatus: 'In Progress',
                ...tenantData,
              },
              tenantRegistrationData,
            }),
          },
        )
          .then(processFetchResponse)
          .then(res => {
            sessionKey = null;

            return res;
          });
      },

      removeTenant(tenantID) {
        get().setTenantList(
          get().tenantList.filter(tenant => tenant.tenantID !== tenantID),
        );
      },

      requestEmailVerify({ email, productCode, customerAWSAccountId }) {
        const params: {
          email: string;
          productCode?: string;
          customerAWSAccountId?: string;
        } = {
          email,
        };

        if (productCode) params['productCode'] = productCode;
        if (customerAWSAccountId)
          params['customerAWSAccountId'] = customerAWSAccountId;

        return fetch(
          `${import.meta.env.VITE_APP_CONTROL_PLANE_URL}/user/email/verification-code`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(params),
          },
        ).then(processFetchResponse);
      },

      selectFirstTenant() {
        get().selectTenant(get().tenantList[0].tenantID);
      },

      selectTenant(tenantID) {
        tenantID
          ? storage.local.set<Nullish<string>>(
              TENANT_CONST.KEYS.STORAGE_CURRENT_TENANT_ID,
              tenantID,
            )
          : storage.local.remove(TENANT_CONST.KEYS.STORAGE_CURRENT_TENANT_ID);

        set({
          currentTenantID: tenantID,
        });
      },

      setTenantList(tenantList) {
        set({ tenantList });

        storage.local.set(TENANT_CONST.KEYS.STORAGE_TENANT_LIST, tenantList);
      },

      setLimitSize(limitSize) {
        set({
          limitSize: {
            ...get().limitSize,
            ...limitSize,
          },
        });
      },
    };
  },
);

export default useTenantStore;
